/**
 * Named API operations.
 *
 * Every workspace action goes through one of these functions so that URL shape,
 * HTTP verb and response contract live in a single place. Only endpoints present
 * in the agreed contract are listed; anything not implemented by the backend is
 * surfaced by the shared error handling instead of being imitated with fake data.
 *
 * Contract implemented here:
 *   POST auth/login   { email, password } -> { user: { id, email, display_name }, csrf_token }
 *   POST auth/logout                      -> 204 (revokes the session server-side)
 *   GET  tenants                          -> { items: [{ id, name, role }] }
 *   GET  topics                           -> { items: [{ id, name, status, created_at }] }
 *   POST topics       { name, include_terms, exclude_terms }
 *   GET  contents                         -> { items: [{ id, revision_id, content_id, title,
 *                                            body, published_at, source_name, source_version }] }
 *   GET  alerts                           -> { items: [{ id, severity, status, summary, created_at }] }
 *   GET  jobs                             -> { items: [{ id, kind, status, created_at }] }
 *   GET  reports                          -> { items: [{ id, title, status, created_at }] }
 */

import { resolveBaseUrl, ApiTransport } from './transport';
import {
  decodeAlertList,
  decodeContentItem,
  decodeContentList,
  decodeJobList,
  decodeJobItem,
  decodeLoginResult,
  decodeLogoutResult,
  decodeReportList,
  decodeTenantList,
  decodeTopic,
  decodeTopicList,
  severityToRule,
  type AlertItem,
  type AlertSeverity,
  type ContentItem,
  type CreateTopicInput,
  type JobItem,
  type LoginResult,
  type LogoutResult,
  type ReportItem,
  type Tenant,
  type Topic,
} from './types';

export interface ContentQuery extends ListQuery {
  readonly keyword?: string;
  readonly source?: string;
  readonly start?: string;
  readonly end?: string;
  readonly scope?: 'all' | 'public' | 'private';
}

/** Common list query parameters accepted by the documented list endpoints. */
export interface ListQuery {
  readonly limit?: number;
  readonly cursor?: string;
}

function nonEmpty(value: string | undefined): string | undefined {
  return value === undefined || value === '' ? undefined : value;
}

function listParams(query: ListQuery | undefined): Record<string, string | number | undefined> {
  const params: Record<string, string | number | undefined> = {};
  if (query?.limit !== undefined) params['limit'] = query.limit;
  if (query?.cursor !== undefined && query.cursor !== '') params['cursor'] = query.cursor;
  return params;
}

/**
 * Live session values for the transport.
 *
 * The transport reads these through callbacks at request time instead of holding a
 * copied value. That removes an ordering hazard: React runs child effects before
 * parent effects, so a page that mounts right after login would otherwise issue its
 * first request before a "sync the copy" effect had run, sending no CSRF or tenant
 * header. There is also only one source of truth for both values.
 */
interface SessionAccessors {
  csrfToken: () => string | null;
  tenantId: () => string | null;
  onUnauthorized: () => void;
}

const accessors: SessionAccessors = {
  csrfToken: () => null,
  tenantId: () => null,
  onUnauthorized: () => undefined,
};

export const transport = new ApiTransport({
  baseUrl: resolveBaseUrl(import.meta.env.VITE_API_BASE_URL),
  getCsrfToken: () => accessors.csrfToken(),
  getTenantId: () => accessors.tenantId(),
  onUnauthorized: () => {
    accessors.onUnauthorized();
  },
});

/** Wired once during bootstrap so any 401 clears the session exactly once. */
export function setUnauthorizedHandler(handler: () => void): void {
  accessors.onUnauthorized = handler;
}

/** Points the transport at the current session values. Called once at start-up. */
export function configureSessionAccessors(next: {
  csrfToken: () => string | null;
  tenantId: () => string | null;
}): void {
  accessors.csrfToken = next.csrfToken;
  accessors.tenantId = next.tenantId;
}

export function apiBaseUrl(): string {
  return transport.baseUrl;
}

/* ------------------------------------------------------------------ auth --- */

export interface LoginInput {
  readonly email: string;
  readonly password: string;
}

/**
 * Signs in and returns the CSRF token plus the user profile.
 * The session cookie is set by the API as HttpOnly; it is never read here.
 *
 * The token comes from the response body only. An earlier revision also accepted
 * an `X-CSRF-Token` response header; that variant is no longer part of the agreed
 * contract, and accepting it meant a session could be marked signed in while
 * holding a token from a source nobody had agreed on. A response without a body
 * token therefore fails loudly instead of producing an unusable session.
 */
export async function login(input: LoginInput, signal?: AbortSignal): Promise<LoginResult> {
  return transport.request('/auth/login', decodeLoginResult, {
    method: 'POST',
    body: { email: input.email, password: input.password },
    withTenant: false,
    // A 401 here means "wrong credentials", not "session expired".
    anonymous: true,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Ends the session **on the server**.
 *
 * This is a real revocation, not a local cleanup: the API takes the CSRF token
 * from `X-CSRF-Token` plus the session/CSRF cookies and marks the session token
 * revoked, so the cookie is dead for anyone who holds a copy. Tenant context is
 * deliberately not sent — a session is not scoped to one tenant, and requiring a
 * tenant header would make sign-out fail exactly when the tenant list is broken.
 *
 * A non-2xx answer is thrown, never swallowed: if the server did not revoke the
 * session, the caller must be able to say so rather than show a clean sign-out.
 */
export async function logout(signal?: AbortSignal): Promise<LogoutResult> {
  return transport.request('/auth/logout', decodeLogoutResult, {
    method: 'POST',
    withTenant: false,
    // A 401 means the session was already gone, which is the desired end state;
    // it must not be re-reported as a fresh "session expired" event.
    anonymous: true,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Tenant list and session probe, at the agreed `GET /tenants`.
 *
 * A 401 means signed out; any other failure is reported as "cannot confirm". The
 * identity router also serves `/auth/tenants` for backend compatibility, but the
 * workspace no longer probes it: a fallback would turn a 404 on the agreed path
 * into an apparently working tenant list, hiding the break on the single call that
 * gates every business page.
 */
export const TENANTS_PATH = '/tenants';

export function fetchTenants(signal?: AbortSignal): Promise<{ items: Tenant[] }> {
  return transport.request(TENANTS_PATH, decodeTenantList, {
    withTenant: false,
    ...(signal === undefined ? {} : { signal }),
  });
}

/* ---------------------------------------------------------------- topics --- */

export function fetchTopics(query?: ListQuery, signal?: AbortSignal): Promise<{ items: Topic[] }> {
  return transport.request('/topics', decodeTopicList, {
    query: listParams(query),
    ...(signal === undefined ? {} : { signal }),
  });
}

export function createTopic(input: CreateTopicInput, signal?: AbortSignal): Promise<Topic> {
  return transport.request('/topics', decodeTopic, {
    method: 'POST',
    body: {
      name: input.name,
      include_terms: [...input.include_terms],
      exclude_terms: [...input.exclude_terms],
      ...(input.subject_profile === undefined ? {} : { subject_profile: input.subject_profile }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
}

/* -------------------------------------------------------------- contents --- */

export function fetchContents(
  query?: ContentQuery,
  signal?: AbortSignal,
): Promise<{ items: ContentItem[]; next_cursor: string | null }> {
  return transport.request('/contents', decodeContentList, {
    // Unset filters are omitted: an empty `start`/`end` is not a datetime and is rejected.
    query: {
      ...listParams(query),
      keyword: nonEmpty(query?.keyword),
      source: nonEmpty(query?.source),
      start: nonEmpty(query?.start),
      end: nonEmpty(query?.end),
      scope: query?.scope,
    },
    ...(signal === undefined ? {} : { signal }),
  });
}

export function fetchContentRevision(id: string, signal?: AbortSignal): Promise<ContentItem> {
  return transport.request(`/contents/revisions/${encodeURIComponent(id)}`, decodeContentItem, {
    ...(signal === undefined ? {} : { signal }),
  });
}

export function fetchContent(id: string, signal?: AbortSignal): Promise<ContentItem> {
  return transport.request(
    `/contents/${encodeURIComponent(id)}`,
    decodeContentItem,
    signal ? { signal } : {},
  );
}

/* ---------------------------------------------------------------- alerts --- */

export interface AlertQuery extends ListQuery {
  readonly severity?: AlertSeverity;
  readonly status?: string;
  readonly unread_only?: boolean;
}

export function fetchAlerts(
  query?: AlertQuery,
  signal?: AbortSignal,
): Promise<{ items: AlertItem[] }> {
  const params: Record<string, string | number | undefined> = listParams(query);
  if (query?.severity) params['severity'] = severityToRule(query.severity);
  if (query?.status) params['status'] = query.status;
  if (query?.unread_only) params['unread_only'] = 'true';
  return transport.request('/alerts/instances', decodeAlertList, {
    query: params,
    ...(signal === undefined ? {} : { signal }),
  });
}

/* ------------------------------------------------------------------ jobs --- */

export function cancelJob(id: string): Promise<JobItem> {
  return transport.request(`/jobs/${encodeURIComponent(id)}/cancel`, decodeJobItem, {
    method: 'POST',
  });
}

export function fetchJobs(query?: ListQuery, signal?: AbortSignal): Promise<{ items: JobItem[] }> {
  return transport.request('/jobs', decodeJobList, {
    query: listParams(query),
    ...(signal === undefined ? {} : { signal }),
  });
}

/* --------------------------------------------------------------- reports --- */

export function fetchReports(
  query?: ListQuery,
  signal?: AbortSignal,
): Promise<{ items: ReportItem[] }> {
  return transport.request('/reports', decodeReportList, {
    query: listParams(query),
    ...(signal === undefined ? {} : { signal }),
  });
}
